import { useState } from "react"
import { apiClient } from "@/lib/api-client"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import BillingPlans from "../components/BillingPlans"
import Checkout from "../components/Checkout"
import type { BillingPlanDto } from "../../dtos/billing-plan.dto"
import { redirectToPayFast } from "../../infrastructure/payfast-redirect"

type BillingCycle = "Monthly" | "Annual"

type PayFastRedirectResultDto = {
  redirectUrl: string
  formData: Record<string, string>
  subscriptionId: string
  signature: string
}

type CreateSubscriptionResponseDto = {
  subscription: unknown
  paymentRedirect: PayFastRedirectResultDto | null
  trialActivated: boolean
}

export default function CreateSubscriptionPage() {
  const organizationService = useOrganizationsService()

  const [currentStep, setCurrentStep] = useState<"plan" | "checkout">("plan")
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanDto | null>(null)
  const [isYearly, setIsYearly] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)

  const organizations = organizationService.organizations || []
  const currentOrg = organizations[0]

  const handlePlanSelect = (plan: BillingPlanDto, yearly: boolean) => {
    setSelectedPlan(plan)
    setIsYearly(yearly)
    setCurrentStep("checkout")
  }

  const handlePayment = async () => {
    if (!selectedPlan || !currentOrg) return

    try {
      setIsProcessing(true)

      const billingCycle: BillingCycle = isYearly ? "Annual" : "Monthly"

      const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
        "/subscription/create",
        {
          planId: String(selectedPlan.id),
          billingInterval: billingCycle,
        },
      )

      if (data.paymentRedirect) {
        redirectToPayFast(
          data.paymentRedirect.redirectUrl,
          data.paymentRedirect.formData,
        )
      }
    } catch (error) {
      console.error("Error creating subscription:", error)

      let errorMessage = "Failed to start payment process. Please try again."

      if (error instanceof Error) {
        if (error.message && error.message.includes("400")) {
          errorMessage = "Invalid subscription details. Please try again."
        } else if (error.message && error.message.includes("404")) {
          errorMessage = "Selected plan not found. Please choose another plan."
        }
      }

      alert(errorMessage)
      setIsProcessing(false)
    }
  }

  const handleBack = () => {
    setCurrentStep("plan")
    setSelectedPlan(null)
  }

  const companyForm = currentOrg
    ? {
        companyName: currentOrg.name,
        streetAddress: currentOrg.physicalAddress?.line1 || "",
        city: currentOrg.physicalAddress?.city || "",
        state: currentOrg.physicalAddress?.state || "",
        zipCode: currentOrg.physicalAddress?.postalCode || "",
        country: currentOrg.physicalAddress?.country || "",
        line2: currentOrg.physicalAddress?.line2,
        postalAddress: "",
        physicalAddress: "",
      }
    : {
        companyName: "",
        streetAddress: "",
        city: "",
        zipCode: "",
        country: "",
        postalAddress: "",
        physicalAddress: "",
      }

  return (
    <div className="container mx-auto py-8 px-4">
      {currentStep === "plan" && (
        <>
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-text mb-2">
              Create New Subscription
            </h1>
            <p className="text-text/70">
              Choose a plan that best fits your organisation's needs
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <BillingPlans onPlanSelect={handlePlanSelect} />
          </div>
        </>
      )}

      {currentStep === "checkout" && selectedPlan && (
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-text mb-2">
              Review & Complete
            </h1>
            <p className="text-text/70">
              Review your subscription details before payment
            </p>
          </div>
          <Checkout
            companyForm={companyForm}
            selectedPlan={selectedPlan}
            isYearly={isYearly}
            onBack={handleBack}
            onComplete={handlePayment}
            isProcessing={isProcessing}
          />
        </div>
      )}
    </div>
  )
}